import React, { useEffect } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { CopilotKit } from '@copilotkit/react-core';
import { Header } from '@features/header/Header';
import { Footer } from '@features/footer/Footer';
import { Sidebar } from '@features/sidebar/Sidebar';
import { useAuthStore } from 'src/store/auth';
import { useApplicationStore } from 'src/store/application';
import { Application } from 'src/services/interfaces/application';
import { useHealthStore } from 'src/store/health';
import { useInsuranceStore } from 'src/store/insurance';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import {
  DependentSchema,
  dependentSchema,
} from '@features/onboarding/schema/onboarding';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { cn } from '@utils/index';
import '@copilotkit/react-ui/styles.css';
import { transformApplicationToOnboardingData } from '@features/onboarding/utils/transformApplicationToOnboardingData';
import { useAI } from 'src/hooks/useAI';
import { LayoutLoading } from 'src/layouts/LayoutLoading';

export const PrivateLayout: React.FC = () => {
  const { isAuthenticated, wasAuthenticated } = useAuthStore();
  const {
    currentStep,
    setData: setOnboardingData,
    isLoaded,
    setCurrentStep,
  } = useOnboardingStore();
  const { applications, getCurrentApplication, updateTierFromApplicationState } = useApplicationStore();
  const { getConditions, getIssueDetails, getIssues, issues, conditions } =
    useHealthStore();
  const {
    plans: insurancePlans,
    getFullPlans,
    getProviders,
  } = useInsuranceStore();
  const { data } = useOnboardingStore();
  const location = useLocation();
  
  // Инициализируем useAI для обработки хуков
  useAI();

  const form = useForm<DependentSchema>({
    // @ts-ignore
    resolver: zodResolver(dependentSchema),
    defaultValues: {
      ...data,
    },
  });

  useEffect(() => {
    if (isAuthenticated) {
      getConditions();
      getIssueDetails();
      getIssues();
      getProviders();
      getFullPlans();
    }
  }, [
    isAuthenticated,
    getConditions,
    getFullPlans,
    getProviders,
    getIssueDetails,
    getIssues,
  ]);

  // Update tier when insurance plans are loaded
  useEffect(() => {
    // if (insurancePlans.length > 0) {
      updateTierFromApplicationState();
    // }
  }, [insurancePlans.length, updateTierFromApplicationState]);

  useEffect(() => {
    if (applications.length > 0 && insurancePlans.length > 0 && !isLoaded) {
      const lastApplication = applications[applications.length - 1];
      if (lastApplication) {
        const transformedData = transformApplicationToOnboardingData(
          lastApplication as Application,
          insurancePlans
        );
        setOnboardingData(transformedData);

        setTimeout(() => {
          useOnboardingStore.getState().resetAllForms(transformedData);
        }, 100);
      }
      if (isLoaded && applications.length) {
        setCurrentStep(0);
      }
    }
  }, [applications, insurancePlans, setOnboardingData, isLoaded]);

  useEffect(() => {
    if (data && Object.keys(data).length > 0 && form && form.reset) {
      const enhancedData = useOnboardingStore
        .getState()
        .enhanceHealthQuestionsData(data, issues, conditions);
      form.reset(enhancedData);
    }
  }, [data, form, issues, conditions]);

  if (!isAuthenticated) {
    return <Navigate to={wasAuthenticated ? '/' : '/registration'} replace />;
  }

  if (location.pathname === '/tester' || location.pathname === '/payment') {
    return (
      <CopilotKit
        showDevConsole={false}
        publicApiKey={import.meta.env.VITE_APP_COPILOT_PUBLIC_KEY || ''}
      >
        <Outlet />
      </CopilotKit>
    );
  }

  return (
    <CopilotKit
      showDevConsole={false}
      publicApiKey={import.meta.env.VITE_APP_COPILOT_PUBLIC_KEY || ''}
    >
      <LayoutLoading />
      <div className="flex min-h-screen max-h-screen flex-col py-3.5 lg:pt-5 pb-6 gap-3.5 lg:gap-6 max-w-[1920px] mx-auto">
        <Header />
        <main className="flex flex-1 justify-between rounded-xl max-lg:mt-[74px] max-xl:mt-[90px] 2xl:max-h-[calc(100vh-240px)] gap-4 md:rounded-4xl pb-0 md:pb-6 2xl:pb-0 mx-6 max-lg:mx-4">
          <section
            className={cn(
              'flex flex-1 flex-col overflow-y-auto 2xl:overflow-y-hidden max-lg:mb-auto max-md:overflow-y-clip mb-0',
            )}
          >
            <Outlet />
          </section>
          <Sidebar />
        </main>
        <Footer />
      </div>
    </CopilotKit>
  );
};
